import { FormError, PageLayout } from '@kcftech/design-system';
import { SpinnerArea } from 'components/spinner/SpinnerArea';
import { NotFound } from 'pages/NotFound';
import * as React from 'react';
import styled from 'styled-components';
import { tokens } from 'styles/tokens';

type FullsizeProps = Readonly<{ $fullHeight: boolean; $fullWidth: boolean }>;

const fullsizeFunc = ({ $fullHeight }: FullsizeProps) =>
    $fullHeight
        ? `
              flex-grow: 1;
              display: flex;
              flex-direction: column;
          `
        : '';

const DetailsContainerWrapper = styled.div<FullsizeProps>`
    padding: inherit;
    ${fullsizeFunc}
`;

const DetailsContent = styled.div<{
    $noPageScroll: boolean;
}>`
    flex-grow: 1;
    background-color: ${tokens.card.background};
    display: flex;
    flex-direction: column;
    border: ${tokens.card.border};
    border-radius: 8px;

    ${({ $noPageScroll }) => ($noPageScroll ? 'overflow-y: hidden;' : '')}
`;

const DetailsErrorWrapper = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    flex-grow: 1;
`;

const DetailsError = styled(FormError)`
    margin: 3rem;
    padding: 3rem;
`;

export type DetailsContainerProps = React.PropsWithChildren<
    Readonly<{
        error?: string;
        invalidURL?: boolean;
        isLoading?: boolean;
        fullHeight?: boolean;
        fullWidth?: boolean;
        noPageScroll?: boolean;
        hideChildren?: boolean;
    }>
>;

const DetailContentInnerWrapper = styled.div<{
    $fullWidth: boolean;
    $noPageScroll: boolean;
}>`
    max-width: 100%;
    width: ${({ $fullWidth }) => ($fullWidth ? 'auto' : 'fit-content')};
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    padding: 3rem 1rem;

    ${({ $noPageScroll }) => ($noPageScroll ? 'overflow-y: hidden;' : '')}
`;

const DetailsSpinner = styled(SpinnerArea)<{
    $fullWidth: boolean;
}>`
    width: ${({ $fullWidth }) => ($fullWidth ? '100%' : 'fit-content')};
`;

/* We will use padding instead of margin in order to avoid a gap between the
    scrollbar and the right edge of the screen. The !important is necessary
    because there are currently problems with overriding styles provided by our
    design system components via 'styled-components'. */
const Layout = styled(PageLayout.Primary)`
    margin-left: 0 !important;
    margin-right: 0 !important;
    padding-left: 1.2rem;
    padding-right: 1.2rem;
`;

export function DetailsPage({
    error,
    invalidURL,
    isLoading = false,
    fullHeight = false,
    fullWidth = false,
    noPageScroll = false,
    children,
    hideChildren,
}: DetailsContainerProps) {
    const content = (
        <>
            {error ? (
                <DetailsContent $noPageScroll={noPageScroll} className="flex-column">
                    <DetailsErrorWrapper>
                        <DetailsError message={error} />
                    </DetailsErrorWrapper>
                </DetailsContent>
            ) : (
                <DetailsSpinner
                    isLoading={isLoading}
                    hideChildren={hideChildren}
                    $fullWidth={fullWidth}
                >
                    <DetailsContent $noPageScroll={noPageScroll}>
                        {children}
                    </DetailsContent>
                </DetailsSpinner>
            )}
        </>
    );

    return (
        <>
            {invalidURL ? (
                <NotFound />
            ) : (
                <Layout className="position-relative scrollable-y kcf-page-layout">
                    <DetailsContainerWrapper
                        className="position-absolute inset-0"
                        $fullHeight={fullHeight}
                        $fullWidth={fullWidth}
                    >
                        <DetailContentInnerWrapper
                            $fullWidth={fullWidth || isLoading}
                            $noPageScroll={noPageScroll}
                        >
                            {content}
                        </DetailContentInnerWrapper>
                    </DetailsContainerWrapper>
                </Layout>
            )}
        </>
    );
}
